
import { supabase } from "@/integrations/supabase/client";

// Verify and accept an invitation
export const acceptInvitation = async (token: string): Promise<boolean> => {
  try {
    // Find the invitation with basic store info
    const { data: invitation, error: fetchError } = await supabase
      .from("employee_invitations")
      .select(`
        *,
        stores!inner(name)
      `)
      .eq("token", token)
      .eq("accepted", false)
      .maybeSingle();

    if (fetchError) {
      console.error("Error fetching invitation:", fetchError);
      return false;
    }

    if (!invitation) {
      console.error("No invitation found with token:", token);
      return false;
    }

    console.log("Found invitation:", invitation);

    // Check if invitation has expired
    if (new Date(invitation.expires_at) < new Date()) {
      console.error("Invitation has expired");
      return false;
    }

    // Get current authenticated user
    const {
      data: { user },
      error: userError,
    } = await supabase.auth.getUser();

    if (userError || !user) {
      console.error("No authenticated user found:", userError);
      return false;
    }

    // Verify email matches
    if (user.email !== invitation.email) {
      console.error(
        "Email mismatch - user:",
        user.email,
        "invitation:",
        invitation.email
      );
      return false;
    }

    // Ensure the role is one of the allowed values
    const employeeRole = invitation.role as
      | "admin"
      | "manager"
      | "staff"
      | "super_admin";

    // Check if employee record already exists
    const { data: existingEmployee, error: checkError } = await supabase
      .from("store_employees")
      .select("*")
      .eq("user_id", user.id)
      .eq("store_id", invitation.store_id)
      .maybeSingle();

    if (checkError) {
      console.error("Error checking existing employee:", checkError);
    }

    if (existingEmployee) {
      // Update existing employee record
      const { error: updateError } = await supabase
        .from("store_employees")
        .update({
          role: employeeRole,
          first_name: invitation.first_name,
          last_name: invitation.last_name,
          email: invitation.email,
          address: invitation.address,
          street: invitation.street,
          street_number: invitation.street_number,
          city: invitation.city,
          postcode: invitation.postcode,
        })
        .eq("id", existingEmployee.id);

      if (updateError) {
        console.error("Error updating employee record:", updateError);
        return false;
      }
    } else {
      // Create new store employee record
      const { data: employee, error: employeeError } = await supabase
        .from("store_employees")
        .insert({
          user_id: user.id,
          store_id: invitation.store_id,
          first_name: invitation.first_name,
          last_name: invitation.last_name,
          role: employeeRole,
          email: invitation.email,
          address: invitation.address,
          street: invitation.street,
          street_number: invitation.street_number,
          city: invitation.city,
          postcode: invitation.postcode,
          invited_by_user_id: invitation.invited_by_user_id,
        })
        .select()
        .single();

      if (employeeError) {
        console.error("Error creating employee record:", employeeError);
        return false;
      }

      console.log("Created employee record:", employee);
    }

    // Mark invitation as accepted
    const { error: updateError } = await supabase
      .from("employee_invitations")
      .update({ accepted: true })
      .eq("id", invitation.id);

    if (updateError) {
      console.error("Error accepting invitation:", updateError);
      return false;
    }

    console.log("Invitation marked as accepted");

    // Add store permission
    const { data: existingPermission } = await supabase
      .from("employee_store_permissions")
      .select("*")
      .eq("user_id", user.id)
      .eq("store_id", invitation.store_id)
      .maybeSingle();

    if (!existingPermission) {
      const { error: permissionError } = await supabase
        .from("employee_store_permissions")
        .insert({
          user_id: user.id,
          store_id: invitation.store_id,
        });

      if (permissionError) {
        console.error("Error adding store permission:", permissionError);
        // Continue even if this fails as it's not critical
      }
    }

    // Get the inviter details for notification email
    const { data: invitedByUser, error: invitedByUserError } = await supabase
      .from("users")
      .select("*")
      .eq("id", invitation.invited_by_user_id)
      .maybeSingle();

    try {
      if (invitedByUser?.email) {
        const { error: emailError } = await supabase.functions.invoke(
          "send-staff-accepted-notification",
          {
            body: {
              inviterEmail: invitedByUser.email,
              inviterFirstName: invitedByUser.first_name,
              staffFirstName: invitation.first_name,
              staffLastName: invitation.last_name,
              staffEmail: invitation.email,
              storeName: invitation.stores.name,
              role: invitation.role,
            },
          }
        );

        if (emailError) {
          console.error("Error sending notification email:", emailError);
          // Don't fail the invitation acceptance if email fails
        }
      }
    } catch (emailError) {
      console.error("Failed to send notification email:", emailError);
      // Don't fail the invitation acceptance if email fails
    }

    return true;
  } catch (error) {
    console.error("Error in accept invitation process:", error);
    return false;
  }
};
